import { createContext } from "@lit/context";
import dayjs from "dayjs";
import { BehaviorSubject } from "rxjs";
import { ScheduleService, TimeSlotService } from "../api";
import { AvailableGroupedProducts, AvailableProductItem, TimeSlotItem } from "../types";

export const productSelectionContextKey = createContext<ProductSelectionContext>("product-selection-context");

export class ProductSelectionContext {

    private readonly _selectedDate: BehaviorSubject<dayjs.Dayjs | null> = new BehaviorSubject<dayjs.Dayjs | null>(dayjs());

    private readonly _isLoadingProducts: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly _availableProducts: BehaviorSubject<AvailableGroupedProducts[]> = new BehaviorSubject<AvailableGroupedProducts[]>([]);
    private readonly _selectedProduct: BehaviorSubject<AvailableProductItem | null> = new BehaviorSubject<AvailableProductItem | null>(null);


    private readonly _isLoadingTimeSlots: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly _selectedTimeSlot: BehaviorSubject<TimeSlotItem | null> = new BehaviorSubject<TimeSlotItem | null>(null);

    public timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    public selectedDate$ = this._selectedDate.asObservable();
    public isLoadingProducts$ = this._isLoadingProducts.asObservable();
    public availableProducts$ = this._availableProducts.asObservable();
    public selectedProduct$ = this._selectedProduct.asObservable();
    public isLoadingTimeSlots$ = this._isLoadingTimeSlots.asObservable();
    public selectedTimeSlot$ = this._selectedTimeSlot.asObservable();

    constructor() {
        this._selectedDate.subscribe({ next: this.handleSelectedDateChanged.bind(this) });
        this._selectedProduct.subscribe({ next: this.handleSelectedProductChanged.bind(this) });
    }

    public init(timeZone: string) {
        this.timeZone = timeZone;
    }

    public setSelectedDate(date: dayjs.Dayjs | null) {
        this._selectedDate.next(date);
    }
    public setSelectedProduct(product: AvailableProductItem | null) {
        console.log("Selected product changed:", product);
        this._selectedProduct.next(product);
    }
    public setSelectedTimeSlot(timeSlot: TimeSlotItem | null) {
        console.log("Selected time slot changed:", timeSlot);
        this._selectedTimeSlot.next(timeSlot);
    }

    private handleSelectedDateChanged(selectedDate: dayjs.Dayjs | null) {
        this._selectedProduct.next(null);
        if (selectedDate) {
            this.fetchProductsForDate(selectedDate);
        } else {
            this._availableProducts.next([]);
        }
    }
    private async handleSelectedProductChanged(selectedProduct: AvailableProductItem | null) {
        this._selectedTimeSlot.next(null);

        if(!selectedProduct) {
            return;
        }

        if(selectedProduct.timeSlotAvailabilities !== undefined) {
            return;
        }

        this._isLoadingTimeSlots.next(true);
        const { data, error } = await TimeSlotService.getTimeSlotAvailability({
            query: {
                ProductId: selectedProduct.id,
                Date: this._selectedDate.value?.format("YYYY-MM-DD"),
            }
        });

        if (error) {
            console.error("Error fetching time slot availability", error);
            this._isLoadingTimeSlots.next(false);
            return;
        }

        selectedProduct.timeSlotAvailabilities = (data ?? []).map(item=>{
            console.log(item.availableCapacity! / item.maxCapacity! * 100);
            return {
                ...item,
                isSelected: false,
                bookingCutOffDate: item.bookingCutOff ? dayjs.utc(item.bookingCutOff).tz(this.timeZone) : null,
                startDate: dayjs.utc(item.start).tz(this.timeZone),
                endDate: dayjs.utc(item.end).tz(this.timeZone),
                percentAvailable: item.availableCapacity! / item.maxCapacity! * 100
            }
        }).sort((a, b) => a.startDate.isBefore(b.startDate) ? -1 : 1);
        this._isLoadingTimeSlots.next(false);
    }

    private async fetchProductsForDate(selectedDate: dayjs.Dayjs) {

        this._isLoadingProducts.next(true);
        const { data, error } = await ScheduleService.getProductsAvailableOnDate({
            query: {
                Date: selectedDate?.format("YYYY-MM-DD")
            }
        });

        if (error) {
            console.error("Error fetching products for date", error);
            this._isLoadingProducts.next(false);
            return;
        }

        this._availableProducts.next(data ?? []);
        this._isLoadingProducts.next(false);
    }
}